<script setup lang="ts">
// Vendor
import type { InvalidSubmissionContext } from "vee-validate";

// Types
import type { BaseForm, TSchema } from "../types/baseform";

const props = defineProps<BaseForm<TSchema>>();
const {
  initialErrors,
  initialValues,
  state,
  submitDisabledOnInvalid,
  submitText,
  validationSchema,
} = props;

const emit = defineEmits<{
  (event: "submit", values: Partial<TSchema>): void;
  (event: "error", payload: InvalidSubmissionContext): void;
}>();

const { meta } = useForm<Partial<TSchema>>({
  initialErrors,
  initialValues,
  validationSchema,
});

const { t } = useI18n();

const isSubmitDisabled = computed(
  () =>
    state === "processing" || (submitDisabledOnInvalid && !meta.value.valid),
);
</script>

<template>
  <DefaultForm
    class="base-form"
    @submit="emit('submit', $event)"
    @error="emit('error', $event)"
    v-bind="props"
  >
    <div v-if="$slots.header" class="mb16">
      <slot name="header" />
    </div>
    <div class="base-form__fields">
      <slot />
    </div>
    <div class="base-form__footer mt16">
      <slot name="footer" />
      <div :class="{ mt16: $slots.footer }">
        <template v-if="$slots.submitButton">
          <slot name="submitButton" />
        </template>
        <Button
          v-else
          class="base-form__submit"
          :disabled="isSubmitDisabled"
          type="submit"
        >
          {{ submitText || t("website.form.submit") }}
        </Button>
      </div>
    </div>
  </DefaultForm>
</template>
